/**
 * useStageDeleteConfirmation — routes the board column header's "Delete Stage"
 * action through the SAME confirmation surface the Stage editor uses (SE-6561).
 *
 * Before this hook the header path (`MainContainer.handleDeleteColumn`) awaited
 * `deletePhase` the moment the menu row was pressed: no dialog, no confirm step.
 * The `DeleteStageDialog` molecule already existed but was reachable only from
 * `StageEditorView`, so the two ways to delete a stage behaved differently.
 *
 * Scope note — the pre-existing occupancy guard in `useKanbanPhaseOperations`
 * ("Cannot delete column with players. Move players first.") is deliberately
 * untouched. It remains the authority: this hook only adds a confirmation in
 * front of it, and surfaces the same block as the Stage editor does
 * (`blocked-has-cards`) so a populated stage cannot be confirmed at all. The
 * rule-impact warning the Stage editor can also raise is NOT resolved here —
 * the board screen does not load automation rules, and inventing a "no rules
 * reference this stage" claim from an unloaded set would be a lie. That gap is
 * tracked separately (SE-6536).
 *
 * Occupancy is counted over the board's RAW card set with the guard's own
 * predicate, NOT over the rendered columns. A column's `count` is the number of
 * open cards that resolved to a player (`generateColumnsData` drops closed
 * cards into the Closed area and skips player-less cards), so a terminal stage
 * holding only closed cards renders as `count: 0` while the guard still counts
 * those cards and refuses. Planning off the rendered count would offer a plain
 * "Delete stage" confirm that the guard then rejects — the confirmation would
 * be lying about what pressing it does.
 * @module features/player-kanban/hooks/useStageDeleteConfirmation
 */

import { useCallback, useMemo, useState } from "react";

import type { DeleteStageDialogContainerProps } from "../components/molecules/DeleteStageDialog";
import type { StageDeletionPlan } from "../utils/stageEditorOps";

/** The stage awaiting confirmation, with the plan resolved at request time. */
interface PendingStageDelete {
  /** The phase id passed to `deletePhase` on confirm. */
  readonly stageId: string;
  /** The stage name, named in the dialog copy. */
  readonly stageName: string;
  /** The resolved plan (plain confirm, or the has-cards block). */
  readonly plan: StageDeletionPlan;
}

/** The minimum a board stage must expose to be named in the confirmation. */
export interface StageDeleteTarget {
  readonly id: string;
  readonly name: string;
}

/** The minimum a board card must expose to be counted against a stage. */
export interface StageDeleteCardRef {
  readonly kanbanPhase?: { readonly id?: string | null } | null;
}

/** Arguments to {@link useStageDeleteConfirmation}. */
export interface UseStageDeleteConfirmationArgs {
  /** The board's phases — the source of the stage name in the dialog copy. */
  readonly stages: readonly StageDeleteTarget[] | undefined;
  /**
   * The board's RAW cards — the exact array `deletePhase`'s guard filters, so
   * the dialog's occupancy claim and the guard can never disagree. Do NOT pass
   * the rendered columns: their counts exclude closed and player-less cards.
   */
  readonly cards: readonly StageDeleteCardRef[] | undefined;
  /** The board service that performs the delete; keeps its own guard. */
  readonly deletePhase: (phaseId: string) => Promise<boolean>;
}

/** The hook's surface: a request handler plus the dialog's props. */
export interface UseStageDeleteConfirmationResult {
  /** Opens the confirmation for a stage. Replaces the old immediate delete. */
  readonly requestDeleteStage: (phaseId: string) => void;
  /** Spread onto `DeleteStageDialog`. */
  readonly deleteStageDialogProps: DeleteStageDialogContainerProps;
}

/**
 * Whether a plan forbids deletion outright (the dialog disables its confirm).
 *
 * Only `blocked-has-cards` is reachable from this hook — the board screen has
 * no page-info signal to raise the Stage editor's `blocked-unknown-occupancy`,
 * and asserting one it cannot observe would be worse than inheriting the
 * guard's own window. The other statuses are still matched so a future plan
 * source cannot slip a blocked plan past the confirm.
 * @param plan - The resolved deletion plan.
 * @returns True when the plan can never be confirmed.
 */
const isBlockedPlan = (plan: StageDeletionPlan): boolean =>
  plan.status === "blocked-has-cards" ||
  plan.status === "blocked-unknown-occupancy";

/**
 * Confirmation state for deleting a stage from the board column header.
 * @param args - {@link UseStageDeleteConfirmationArgs}.
 * @param args.stages - The board's phases (source of the stage name).
 * @param args.cards - The board's raw cards (source of occupancy).
 * @param args.deletePhase - The board service that performs the delete.
 * @returns {@link UseStageDeleteConfirmationResult}.
 */
export const useStageDeleteConfirmation = ({
  stages,
  cards,
  deletePhase,
}: UseStageDeleteConfirmationArgs): UseStageDeleteConfirmationResult => {
  const [pendingDelete, setPendingDelete] = useState<PendingStageDelete | null>(
    null
  );
  const [isConfirming, setIsConfirming] = useState(false);

  /**
   * Resolve the deletion plan for a stage against the CURRENT card set, using
   * the guard's own predicate (`useKanbanPhaseOperations.deletePhase`).
   * @param phaseId - The stage being deleted.
   * @returns The plan, or null when the stage is not on the board.
   */
  const resolvePlan = useCallback(
    (phaseId: string): StageDeletionPlan | null => {
      const isOnBoard = (stages ?? []).some(stage => stage.id === phaseId);
      if (!isOnBoard) return null;

      const cardCount = (cards ?? []).filter(
        card => card.kanbanPhase?.id === phaseId
      ).length;

      return cardCount > 0
        ? { status: "blocked-has-cards", cardCount }
        : { status: "ready" };
    },
    [stages, cards]
  );

  const requestDeleteStage = useCallback(
    (phaseId: string) => {
      const stage = stages?.find(candidate => candidate.id === phaseId);
      const plan = stage ? resolvePlan(phaseId) : null;
      // No stage means no name to put in the copy. Opening an unnamed
      // confirmation would be worse than doing nothing, and deleting silently
      // is exactly the defect this hook exists to remove.
      if (!stage || !plan) return;

      setPendingDelete({ stageId: phaseId, stageName: stage.name, plan });
    },
    [stages, resolvePlan]
  );

  const handleClose = useCallback(() => {
    setPendingDelete(null);
    setIsConfirming(false);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!pendingDelete) return;
    // Unreachable through the UI (the confirm is disabled for a blocked plan),
    // but a programmatic confirm must not slip past the block either.
    if (isBlockedPlan(pendingDelete.plan)) {
      setPendingDelete(null);
      return;
    }

    // The plan was resolved when the menu row was pressed; cards can arrive (a
    // later card page, a drag landing) and the board can be switched while the
    // dialog sits open. Re-resolve against the current set, and if the stage
    // has since gained cards or left the board, show the block instead of
    // sending a stale id into a mutation the guard would reject anyway.
    const currentPlan = resolvePlan(pendingDelete.stageId);
    if (!currentPlan) {
      setPendingDelete(null);
      return;
    }
    if (isBlockedPlan(currentPlan)) {
      setPendingDelete({ ...pendingDelete, plan: currentPlan });
      return;
    }

    setIsConfirming(true);
    try {
      await deletePhase(pendingDelete.stageId);
    } catch {
      // `deletePhase` already routes failures into the board's error state,
      // which the screen renders. Swallowing here keeps the rejection from
      // escaping an event handler.
    } finally {
      setIsConfirming(false);
      setPendingDelete(null);
    }
  }, [pendingDelete, deletePhase, resolvePlan]);

  const onConfirm = useCallback(() => {
    void handleConfirm();
  }, [handleConfirm]);

  const deleteStageDialogProps = useMemo<DeleteStageDialogContainerProps>(
    () => ({
      isOpen: pendingDelete !== null,
      stageName: pendingDelete?.stageName ?? "",
      plan: pendingDelete?.plan ?? null,
      isConfirming,
      onConfirm,
      onClose: handleClose,
    }),
    [pendingDelete, isConfirming, onConfirm, handleClose]
  );

  return { requestDeleteStage, deleteStageDialogProps };
};
